import React from "react";

import { images } from "../../constants";
import "./SaltwaterPapermaking.css";
import { SubHeading } from "../../components";
import Intro from "../Video/Video";

const side_by_side_style = {
  display: "flex",
};

const saltwater_paper_style = {
  margin: "auto",
};

const SaltwaterPapermaking = () => (
  <div className="app__bg app__wrapper section__padding">
    <div className="app__wrapper_info">
      <h1 className="headtext__cormorant">Saltwater Papermaking</h1>

      <div className="app__author-content">
        <SubHeading title="Introduction"></SubHeading>
        <div className="app__author-content_quote">
          <p className="p__opensans_lower_case flex__center">
            Papermaking is a water intensive industry. Not only does water help
            facilitate the movement of the pulp through the machines, water acts
            as a lubricant, medium for suspension, cooling and heating method,
            cleaning, and sealant (among other things) within the industrial
            paper production process (Jung). “As much as 25,000 gallons of water
            is required to produce a ton of paper” (ZGF Filters). To put that
            into perspective, a piece of A4 paper can consume 10-20 litres of
            water (Paper/on the rocks). Although sources such as Two Sides try
            to mitigate this expansive use of water in the paper production
            process by citing that “Between 1959 and 2017, there was an 81%
            reduction in the average treated effluent [waste water] flow volume
            at pulp and paper mills within the United States,” it still remains
            that the paper industry consumes a large amount of fresh water. Take
            for instance this graph in Andrew Krosofsky’s essay, “Water
            Consumption in Paper Industry“ Fig. 8.4. Although the graph shows
            that from 1990 to 2008 the demand for fresh water in paper
            production had been reduced, the graph indicates that there has been
            a steady increase in paper production since 1990. According to
            Orville D. Mussey, “The paper industry in the United States has been
            growing at a rapid rate. It has increased about tenfold in the last
            50 years and has doubled every 15 years…. The nationwide paper
            consumption for 1955 has been estimated at 31,700,000 tons.”
            Moreover even if the amount of fresh water used in paper production
            is recycled consecutively three or five times within a given cycle,
            there is still the issue of waste water entering into fresh water
            supplies. According to the Clean Water Action Council, “As a result
            of recycling PCB-containing carbonless copy paper, area mill
            operations discharged about 250,000 pounds of PCB's contaminating
            the sediment in the lower Fox River.” An estimated 160,000 pounds of
            PCB's left the river and entered Green Bay and Lake Michigan by
            2006, and wasn’t finished being cleaned up until the beginning of
            2023. The use of fresh water in the paper making industry, and the
            contamination of fresh water sites due to waste water run off from
            paper mills, made me interested in researching alternative,
            sustainable papermaking solutions. A common denominator I found
            during my research was that all the sources I consulted mentioned
            the use of fresh water, but not salt water, even though salt water
            would still provide many of the same forms and functions sought in
            fresh water such as mobility, movement, and transportation.
          </p>
        </div>
        <SubHeading title="Beach day" />
        <Intro />
        <SubHeading title="Results" />
        <img
          style={saltwater_paper_style}
          src={images.SaltWaterPaperComparison}
          alt="comparison of saltwater paper to normal paper"
        ></img>
        <p className="p__opensans_lower_case flex__center">
          See the paper above as the saltwater paper, and the paper underneath a
          control variable of commercial A4 paper. Similarly to the freshwater
          recycled A4 paper, the saltwater A4 paper performed the same when it
          came to the ability to go through the printer and be printed upon.
          There is no visible difference to the freshwater or saltwater paper in
          terms of thickness and performance. The only difference is that the
          saltwater paper had residue of sand on it, but this was only due to
          the nature of the environment in which it was made (on the beach). As
          a part of my beach cleanup after making the paper at Santa Monica
          beach, I took a gallon of leftover salt water from my paper pulp
          basin. I then made paper at home using the salt water and remaining
          paper pulp, and what I found was that there was no sand residue in the
          saltwater paper made at home, and that the smell, touch, and texture
          of the paper was exactly the same as that of the freshwater paper.
        </p>
        <br />
        <img
          style={saltwater_paper_style}
          src={images.FreshWaterSaltWaterPaperComparison}
          alt="Comparison between freshwater and saltwater paper"
        ></img>
        <br />
        <p className="p__opensans_lower_case flex__center">
          See on the left the freshwater paper, and on right the saltwater
          paper. The only way to tell the freshwater and saltwater paper apart
          is by licking the two papers. The saltwater paper tastes salty while
          the freshwater paper has no taste. The touch is the same, there is no
          noticeable or distinct odor for either paper. The only reason the
          saltwater paper on the right looks more pigmented with speckles of old
          text, is because of an issue with the blending/pulp making process as
          I needed to use a portable blender for outside use (which had less
          strength than a regular blender). *Note, after a month, the saltwater
          paper gradually and slowly lost its salty tasete.
        </p>
        <SubHeading title="Discussion" />
        <p className="p__opensans_lower_case flex__center">
          The implementation of a salt water based paper production system may
          not be a viable solution as of yet for the papermaking industry due to
          the delicate nature of the machinery used in the commercial paper
          production system. According to Konrad Olejnik, “Water is [a]
          particularly important substance in papermaking. It plays essential
          role[s] in fibers transportation, equipment cleaning, lubrication,
          cooling and in [the] development of a product quality.” He argues that
          efforts to reduce fresh water consumption is important as it would be
          both economically and environmentally beneficial, however warns that
          “The resulting changes in the technological equilibrium have a direct
          effect on the production process,.... As a consequence, it may lead
          to: Reduction in retention of the furnish on the paper machine wire,
          Higher corrosion of equipment, Precipitation of deposits,” etc., etc.,
          (See Konrad Olejnik for full list). Corrosion of equipment is an
          interesting concern I found, as one of the most apparent reasons I saw
          for salt water not being more widely used was because of salt water’s
          corrosive qualities relative to that of fresh water. According to The
          Admiral Plumber, “Salt can remain in your plumbing system for a long
          period of time, either dissolved in saltwater or as salt crystals
          themselves. Salt will react with the lining of your pipes to cause it
          to corrode. Corrosion deteriorates the pipe materials, causing them to
          become frail and weak, unable to hold water pressures to a certain
          extent, and eventually burst or leak.” According to SAMCO, a company
          which specializes in industrialized paper production machinery, “One
          of the important ways industrial facilities in the pulp and paper
          industry can reduce their water usage is by ensuring water-centric
          machinery is in the best possible shape. This might mean you need to:
          regularly check and replace your spray nozzles… ensure aging
          infrastructure is replaced… [and ensure] boiler water is properly
          treated.” Although this source may be a bit biased as they are a
          company which sells services for paper industry machine repairs, other
          sources seem to agree that the most cost effective and efficient way
          to manage the paper industry margins is by ensuring the longevity of
          the machines. Therefore, I decided to test out exactly how detrimental
          corrosive salt water may be to a relatively durable metal such as
          stainless steel. For this experiment I took a piece of stainless steel
          metal (an IKEA fork), and measured out exactly 13 grams of the metal
          pieces. I then placed each 13g metal piece into a sterilized glass
          jar, one in fresh water and the other in salt water.
        </p>
        <br />
        <br />
        <SubHeading title="The result:"></SubHeading>
        <img src={images.OtherJars} alt="Other comparison of the jars" />
        <br />
        <p className="p__opensans_lower_case flex__center">
          After roughly 1-2 months, it is clear that the salt water has created
          rust and the metal has begun showing signs of corrosion. (To the left,
          image of the salt water sample. To the right image of the fresh water
          sample.)
        </p>
        <br />
        <div style={side_by_side_style}>
          <img
            className="comparison_img"
            src={images.SaltwaterForkCorrosion}
            alt="Fork corrosion from saltwater in few months"
            style={saltwater_paper_style}
          />
          <img
            src={images.FreshwaterForkCorrosion}
            alt="Fork corrosion from freshwater in few months"
            style={saltwater_paper_style}
          />
        </div>
        <br />
        <p className="p__opensans_lower_case flex__center">
          Given the amount of corrosion which has occurred in such a short
          period of time, it demonstrates that salt water is not ideal for when
          working with delicate heavy machinery. However, with technological
          problem solving and innovative engineering, it may be possible to
          create papermaking/pulp making machinery out of plastic or
          non-corrosive materials, to accommodate for a shift in paper and pulp
          production. Aside from the corrosive tendency of salt water on the
          paper making machinery, there are concerns that water hardness/water
          contaminants may lead towards decreased quality of the final product.
          According to H. Jung and D. Pauly, “Due to the increasing use of
          recovered paper and the reduced freshwater consumption, constituents
          known as detrimental substances have accumulated in water circuits,
          leading to growing problems in the papermaking process. They can
          result in a reduction in additive efficiency, a reduction in optical
          and strength properties, negative effects on drainage and paper
          drying, negative impacts on sizing, odor formation, deposits, and/or
          foam generation.” Although, in creating my own saltwater paper I did
          not notice any of the potential problems listed above with the
          resulting saltwater paper. Paper drying remained consistent (1-2
          days), sizing remained the same between the freshwater and saltwater
          paper, and there was no odor or deposit formation (or foam
          generation). Although the small scale at which I performed this test
          may limit my overall results and observations, based on my research
          and understanding, the biggest obstacle and hurdle towards
          implementing salt water for papermaking use is the water hardness,
          which renders the machinery prone to mineral buildup and shortens
          machinery lifespan. However, funny enough, according to an article
          titled “Water Damage: How Will Your Documents Fare?”, “Cellulose-based
          documents affected by saltwater are not as vulnerable to mold growth
          as those affected by freshwater are,” demonstrating a potential
          advantage to salt water and paper. In fact, there may be some benefits
          to salt water. See here from Alternative Processes: “How to Make Salt
          Prints.”According to the article, “Salt printing is one of the
          earliest photographic processes in history. It was invented by an
          Englishman named William Henry Fox Talbot in the early 1830s and was
          once the go-to method for printing negatives.” In the processes being
          described within the article, the steps include adding table salt (and
          silver nitrate) to fresh water in order to sensitize a sheet of paper
          which will then be used to transfer on the negative image. This
          demonstrates that there are other practical and artistic uses of salt
          water considering the scarcity and value of fresh water in our current
          climate. For now, I would recommend salt water as a sustainable
          alternative to those seeking to try handmade paper making at home or
          for school/education programs. With regards to the paper and pulp
          industry, in his article, “Paper Machine Water Efficiency,” JOHN A.
          NEUN provides a list of some 15 “APPROACHES TO EFFICIENCY,” in which
          he give practical solutions on ways to reduce the amount of water and
          wood which is used, and to reuse the amount of water in the
          circulation system before it is sent out as waste. Some notable
          approaches he suggests are, “1. Reduce shower pressure during
          operation, Use smaller nozzles at higher pressure to save about 40
          percent of the water used, [and] Reuse filtration of white water to
          save fiber and create cleaner white water for paper machine use.”
          Outside of that, perhaps formative and fundamental changes may need to
          be made to the papermaking machinery–of course, weighing the possible
          benefits and potential for harm with regards to switching from fresh
          water to salt water paper production before doing so. Hopefully this
          small research question will serve to generate more substantial
          scholarship upon this potential topic of interest.
        </p>
      </div>
    </div>
  </div>
);

export default SaltwaterPapermaking;
